interface CoTaskrLogoProps {
  size?: 'sidebar' | 'footer' | 'md' | 'login' | 'lg';
  className?: string;
  /** Use app brand CSS variables (--brand, --brand-dark) — sidebar */
  theme?: 'default' | 'brand';
  animated?: boolean;
  /** Collapsed sidebar — orb mark only (no wordmark). */
  iconOnly?: boolean;
}

const brandGradient = {
  backgroundImage: 'linear-gradient(135deg, var(--brand-dark), var(--brand))',
};

const brandOrbBg = {
  background: 'linear-gradient(135deg, var(--brand-dark), var(--brand))',
};

export default function CoTaskrLogo({
  size = 'lg',
  className = '',
  theme = 'default',
  animated = true,
  iconOnly = false,
}: CoTaskrLogoProps) {
  const sizes = {
    lg: {
      c: 'text-7xl',
      orbWrap: 'w-16 h-16 -ml-2.5',
      orbInner: 'w-16 h-16',
      orbIcon: 'w-9 h-9',
      word: 'text-5xl ml-1',
      ring: 'ring-4',
    },
    login: {
      c: 'text-[3.25rem] leading-none',
      orbWrap: 'w-[3.25rem] h-[3.25rem] -ml-2',
      orbInner: 'w-[3.25rem] h-[3.25rem]',
      orbIcon: 'w-7 h-7',
      word: 'text-[2.35rem] leading-none ml-1',
      ring: 'ring-[3px]',
    },
    md: {
      c: 'text-5xl',
      orbWrap: 'w-11 h-11 -ml-2',
      orbInner: 'w-11 h-11',
      orbIcon: 'w-6 h-6',
      word: 'text-3xl ml-0.5',
      ring: 'ring-2',
    },
    sidebar: {
      c: 'text-[24px] leading-none',
      orbWrap: 'w-7 h-7 -ml-0.5',
      orbInner: 'w-7 h-7',
      orbIcon: 'w-4 h-4',
      word: 'text-[18px] leading-none ml-0.5 whitespace-nowrap',
      ring: '',
    },
    footer: {
      c: 'text-[13px] leading-none',
      orbWrap: 'w-[18px] h-[18px] -ml-0.5',
      orbInner: 'w-[18px] h-[18px]',
      orbIcon: 'w-[10px] h-[10px]',
      word: 'text-[12px] leading-none ml-0.5 whitespace-nowrap',
      ring: '',
    },
  }[size];

  const isBrand = theme === 'brand';
  const letterClass = isBrand
    ? `font-black bg-clip-text text-transparent ${sizes.c}`
    : `font-black bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 bg-clip-text text-transparent ${sizes.c}`;

  const wordClass = isBrand
    ? `font-black bg-clip-text text-transparent ${sizes.word}`
    : `font-black bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 bg-clip-text text-transparent ${sizes.word}`;

  const orbAnimClass = animated ? 'cotaskr-logo-orb' : '';
  const checkAnimClass = animated ? 'cotaskr-logo-check' : '';

  if (iconOnly) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div
          className={`${orbAnimClass} relative shrink-0 hover:scale-105 transition-transform duration-300 ${sizes.orbWrap}`}
        >
          {!isBrand && (
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 translate-x-1 translate-y-1 opacity-20 blur-sm" />
          )}
          <div
            className={
              isBrand
                ? `relative flex items-center justify-center rounded-full shadow-sm ${sizes.orbInner}`
                : `relative flex items-center justify-center rounded-full shadow-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 ${sizes.orbInner} ${sizes.ring} ring-blue-100/90`
            }
            style={isBrand ? brandOrbBg : undefined}
          >
            <svg
              className={`text-white ${sizes.orbIcon}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                className={checkAnimClass}
                pathLength={1}
                strokeDasharray={animated ? 1 : undefined}
                strokeDashoffset={animated ? 1 : 0}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-start gap-0 ${className}`}>
      <div className={letterClass} style={isBrand ? brandGradient : undefined}>
        C
      </div>
      <div
        className={`${orbAnimClass} relative shrink-0 hover:scale-105 transition-transform duration-300 ${sizes.orbWrap}`}
      >
        {!isBrand && (
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 translate-x-1 translate-y-1 opacity-20 blur-sm" />
        )}
        <div
          className={
            isBrand
              ? `relative flex items-center justify-center rounded-full shadow-sm ${sizes.orbInner}`
              : `relative flex items-center justify-center rounded-full shadow-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 ${sizes.orbInner} ${sizes.ring} ring-blue-100/90`
          }
          style={isBrand ? brandOrbBg : undefined}
        >
          <svg
            className={`text-white ${sizes.orbIcon}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              className={checkAnimClass}
              pathLength={1}
              strokeDasharray={animated ? 1 : undefined}
              strokeDashoffset={animated ? 1 : 0}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>
      <div className={wordClass} style={isBrand ? brandGradient : undefined}>
        taskr
      </div>
    </div>
  );
}
